import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface TravelRequest {
  id: string
  request_id: string
  traveler: string
  department: string
  destination: string
  purpose: string
  departure_date: string
  return_date: string
  estimated_budget: number
  status: string
}

interface Booking {
  id: string
  booking_id: string
  booking_type: string
  vendor: string
  confirmation_number: string
  check_in: string
  amount: number
  status: string
}

export default function TravelPage() {
  const [requests, setRequests] = useState<TravelRequest[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [reqRes, bookRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/travel/requests'),
      fetch(API_BASE + '/travel/bookings'),
      fetch(API_BASE + '/travel/statistics')
    ])
    if (reqRes.ok) setRequests((await reqRes.json()).travel_requests || [])
    if (bookRes.ok) setBookings((await bookRes.json()).bookings || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': case 'confirmed': case 'completed': return 'bg-green-100 text-green-800'
      case 'submitted': case 'pending': return 'bg-blue-100 text-blue-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'rejected': case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Corporate Travel</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Travel Request</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Requests</div>
              <div className="text-2xl font-bold">{statistics.total_travel_requests}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Approval</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending_approval}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Confirmed Bookings</div>
              <div className="text-2xl font-bold text-green-600">{statistics.confirmed_bookings}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Expenses YTD</div>
              <div className="text-2xl font-bold text-blue-600">${statistics.total_expenses_ytd?.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending Reimbursement</div>
              <div className="text-2xl font-bold text-purple-600">${statistics.pending_reimbursement?.toLocaleString()}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Travel Requests</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trip</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Budget</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {requests.slice(0, 12).map(req => (
                  <tr key={req.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{req.destination}</div>
                      <div className="text-xs text-gray-500">{req.traveler} - {req.department}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{req.departure_date}</div>
                      <div className="text-xs text-gray-500">to {req.return_date}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium">${req.estimated_budget?.toLocaleString()}</div>
                      <div className="text-xs text-gray-500 capitalize">{req.purpose.replace(/_/g, ' ')}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(req.status)}`}>{req.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Bookings</h3>
            <div className="space-y-3">
              {bookings.slice(0, 10).map(book => (
                <div key={book.id} className="p-3 bg-gray-50 rounded">
                  <div className="flex justify-between items-start mb-1">
                    <span className="font-medium text-sm capitalize">{book.booking_type}</span>
                    <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(book.status)}`}>{book.status}</span>
                  </div>
                  <div className="text-xs text-gray-500">{book.vendor}</div>
                  <div className="flex justify-between items-center mt-2">
                    <span className="text-xs font-mono text-gray-600">{book.confirmation_number}</span>
                    <span className="text-xs font-medium">${book.amount?.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
